<!-- FollowersForm.vue -->
<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import api from "@/services/api";
import { useDashboardStore } from "@/stores/useDashboardStore";

const dashboardStore = useDashboardStore();

const props = defineProps({
  isVisible: { type: Boolean, default: true },
});

// Estado
const isLoading = ref(false);
const error = ref(null);
const followers = ref([]); // lista de quem me segue
const processingId = ref(null); // id do usuário em operação (remover)

const userStore = useUserStore();

// 🔍 termo de busca local
const searchTerm = ref("");

// se há seguidores (independente da busca)
const hasItems = computed(() => followers.value && followers.value.length > 0);

// lista filtrada pela busca
const filteredFollowers = computed(() => {
  const q = searchTerm.value.trim().toLowerCase();
  if (!q) return followers.value;

  return followers.value.filter((u) => {
    const userName = (u.userName || "").toLowerCase();
    const fullName = (u.fullName || "").toLowerCase();
    return userName.includes(q) || fullName.includes(q);
  });
});

// 🔹 Helper de avatar — agora **somente** usa foto real; sem pravatar
function buildUserAvatar(photoPath) {
  let path = photoPath || "";

  // Se não houver caminho de foto, não retorna nada (fallback será ícone)
  if (!path) {
    return null;
  }

  // Se já for URL absoluta, devolve direto
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  // Monta URL relativa em cima da mesma base do backend (tirando o /api do final)
  let base = api.defaults.baseURL || "";
  base = base.replace(/\/api$/, "").replace(/\/+$/, "");

  if (!path.startsWith("/")) path = "/" + path;

  return base + path;
}

async function fetchFollowers() {
  const currentUserId = userStore.currentUser?.userId;
  if (!currentUserId) {
    error.value = "Não foi possível identificar o usuário logado.";
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    // ✅ Endpoint correto para QUEM ME SEGUE (usuário logado)
    const res = await api.get("/Follow/followers");
    const data = res.data;

    // DTO esperado: { userId, fullName/userName, photoPath }
    followers.value = Array.isArray(data)
      ? data
          .filter((u) => (u.userName || "").toLowerCase() !== "sys")
          .map((u) => ({
            userId: u.userId,
            userName: u.userName || "",
            fullName: u.fullName || "",
            avatar: buildUserAvatar(u.photoPath), // se não houver foto, fica null
          }))
      : [];
  } catch (err) {
    console.error("Erro ao carregar lista de seguidores:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Erro ao carregar seus seguidores.";
  } finally {
    isLoading.value = false;
  }
}

// 🔹 Remover seguidor (DELETE /Follow/{targetUserId})
async function handleRemoveFollower(targetUserId) {
  if (!targetUserId || processingId.value) return;

  processingId.value = targetUserId;
  error.value = null;

  try {
    await api.delete(`/Follow/${targetUserId}`);

    // 🔹 remove da lista local
    followers.value = followers.value.filter((u) => u.userId !== targetUserId);

    // 🔹 Atualiza dashboard (contagens, etc.)
    await dashboardStore.fetchDashboard();
  } catch (err) {
    console.error("Erro ao remover seguidor:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Não foi possível remover este seguidor.";
  } finally {
    processingId.value = null;
  }
}

// Carrega quando o componente for exibido
watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      fetchFollowers();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.isVisible) {
    fetchFollowers();
  }
});
</script>

<template>
  <div v-show="isVisible" class="follow-form-container">
    <h3 class="view-title text-center">Seguidores</h3>
    <p class="view-subtitle text-center">
      Pessoas que estão acompanhando você no Litterae.
    </p>

    <!-- 🔍 Campo de busca – estilo idêntico ao SearchTagInput.vue -->
    <div class="search-input-wrapper" v-if="hasItems">
      <div class="icon-wrapper">
        <svg
          aria-hidden="true"
          class="search-icon"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </div>

      <div class="search-text-input">
        <input
          v-model="searchTerm"
          type="text"
          id="followers-search"
          placeholder="Buscar seguidores..."
          class="search-input"
        />
      </div>
    </div>

    <!-- Mensagem de erro -->
    <div
      v-if="error"
      class="mt-2 mb-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700"
    >
      {{ error }}
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center py-8 text-slate-400 text-xs flex-1"
    >
      <div
        class="h-10 w-10 rounded-full border-2 border-indigo-300 border-t-transparent animate-spin mb-3"
      ></div>
      Carregando lista de seguidores...
    </div>

    <!-- Área rolável da lista / empty state -->
    <div v-else class="list-scroll-container">
      <!-- Lista de seguidores -->
      <ul v-if="hasItems" class="space-y-3">
        <li
          v-for="user in filteredFollowers"
          :key="user.userId"
          class="rounded-xl px-3 py-2 flex items-center gap-3"
        >
          <!-- Avatar ou ícone padrão -->
          <div class="flex-shrink-0">
            <!-- Mantém avatar real quando existir -->
            <img
              v-if="user.avatar"
              :src="user.avatar"
              class="h-9 w-9 rounded-full object-cover"
              alt="Avatar"
            />
            <!-- Fallback: ícone fas fa-user no lugar do pravatar -->
            <div
              v-else
              class="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-500"
            >
              <i class="fas fa-user text-sm"></i>
            </div>
          </div>

          <div class="flex-1 min-w-0">
            <!-- USERNAME (em cima, destaque primário) -->
            <p class="text-sm font-semibold text-slate-800 truncate">
              {{ user.userName || "@" + user.userId }}
            </p>

            <!-- FULLNAME (embaixo, menor e suave) -->
            <p
              v-if="user.fullName"
              class="text-xs text-slate-500 truncate"
              :title="user.fullName"
            >
              {{ user.fullName }}
            </p>
          </div>

          <!-- 🔹 Botão Remover (soft red) -->
          <button
            type="button"
            class="remove-btn"
            @click="handleRemoveFollower(user.userId)"
            :disabled="processingId === user.userId"
          >
            Remover
          </button>
        </li>
      </ul>

      <!-- Empty state – nenhum seguidor -->
      <div
        v-else
        class="flex flex-col items-center justify-center text-center py-10 text-slate-400"
      >
        <div
          class="h-20 w-20 rounded-full bg-slate-50 flex items-center justify-center mb-4"
        >
          <i class="fas fa-user-friends fa-lg text-slate-300"></i>
        </div>
        <p class="text-sm font-semibold text-slate-600 mb-1">
          Ainda não há seguidores por aqui
        </p>
        <p class="text-[11px] text-slate-400 max-w-xs">
          À medida que outros leitores começarem a seguir você, eles aparecerão
          nesta lista.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.follow-form-container {
  padding: 0.75rem 0.5rem 0.5rem;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0; /* importante para o flex + scroll funcionar dentro do modal */
}

.view-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.view-subtitle {
  font-size: 0.85rem;
  color: var(--text-light);
  margin-top: 0.15rem;
  margin-bottom: 0.6rem;
}

.search-input-wrapper {
  margin-bottom: 1rem;
}

.search-icon {
  width: 1rem;
}

.search-text-input {
  /* wrapper apenas para manter a mesma estrutura do SearchTagInput.vue */
}

.search-input {
  height: 34px;
}

/* ============================
   ÁREA ROLÁVEL DA LISTA
============================ */
.list-scroll-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.25rem;
}

/* ============================
   BOTÃO REMOVER (soft red style)
============================ */
.remove-btn {
  font-size: 11px;
  padding: 0.4rem 0.9rem;
  border-radius: 0.5rem;
  font-weight: 600;
  min-width: 110px;
  text-align: center;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;

  background-color: #ffe5e5;
  color: var(--danger-text);
}

.remove-btn:hover:not(:disabled) {
  background-color: #ffd6d6;
}

.remove-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.remove-btn:focus,
.remove-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

.remove-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.remove-btn:disabled:hover {
  cursor: not-allowed;
}
</style>